import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowUpRight } from "lucide-react";
import { checkoutStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function BillingSuccess() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      if (!sessionId) return;
      for (let i = 0; i < 20; i++) {
        if (cancelled) return;
        try {
          const s = await checkoutStatus(sessionId);
          setStatus(s);
          setAttempts(i + 1);
          if (s.payment_status === "paid" || s.status === "expired") {
            await refresh();
            setDone(true);
            return;
          }
        } catch {/* keep polling */}
        await new Promise((r) => setTimeout(r, 2000));
      }
      setDone(true);
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = status?.payment_status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full border border-border bg-card rounded-md p-10 text-center">
        {!done ? (
          <Loader2 size={32} className="mx-auto text-primary animate-spin" />
        ) : paid ? (
          <CheckCircle2 size={36} className="mx-auto text-status-approved" strokeWidth={1.5} />
        ) : (
          <AlertTriangle size={36} className="mx-auto text-secondary" strokeWidth={1.5} />
        )}
        <h1 className="serif text-2xl mt-5 text-primary">
          {!done ? "Confirming payment…" : paid ? "Plan activated" : "We're still processing"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {!done
            ? `Checking with Stripe (attempt ${attempts}/20)`
            : paid
              ? `Your ${status?.plan} plan is now active for 30 days.`
              : "Your payment didn't complete in time. If you were charged, refresh your billing page in a few minutes."}
        </p>
        <Link to="/app/billing" className="mt-7 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90">
          Back to billing <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full border border-border bg-card rounded-md p-10 text-center">
        <AlertTriangle size={36} className="mx-auto text-secondary" strokeWidth={1.5} />
        <h1 className="serif text-2xl mt-5 text-primary">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No worries — your current plan is unchanged.</p>
        <Link to="/app/billing" className="mt-7 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90">
          Back to billing <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
